/*
 * blog-toc.js — table of contents + scroll-spy for the custom blog article template.
 *
 * WHY vanilla + defer (not a module): it must not touch the theme's import map, and it
 * runs on one page type only. It builds the contents list from the article's <h2> headings
 * (Joe never tags anything), assigns stable ids, and highlights the section currently being
 * read. Highlight geometry uses a single "reading line" a quarter of the way down the
 * viewport — that answers "which section am I reading?", which heading-based
 * IntersectionObserver does not. Scroll-spy is gated off below 990px, where the contents
 * collapse into a native <details> accordion.
 *
 * If anything throws, every nav is left hidden and the page degrades to its no-JS state
 * (a plain 2-column article), because the navs are rendered hidden and only JS unhides them.
 */
(function () {
  var prose = document.querySelector('[data-article-prose]');
  if (!prose) return;

  var nav = document.querySelector('[data-article-toc]');
  var mobile = document.querySelector('[data-article-toc-mobile]');
  if (!nav && !mobile) return;

  var MIN = nav ? parseInt(nav.getAttribute('data-min-headings') || '3', 10) : 3;
  var desktopMQ = window.matchMedia('(min-width: 990px)');

  var headings = [];
  var links = []; // desktop links only (drive the reveal-into-view)
  var tops = [];
  var activeIndex = -1;
  var ticking = false;
  var suppressUntil = 0;
  var spyOn = false;
  var used = new Set();

  function slugify(t) {
    return t
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/['`"‘’“”]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/g, '');
  }

  function assignId(h, i) {
    if (h.dataset.tocId) { used.add(h.dataset.tocId); return h.dataset.tocId; }
    var base = slugify(h.textContent) || ('section-' + (i + 1));
    if (/^[0-9]/.test(base)) base = 's-' + base; // valid HTML id AND valid CSS selector
    var id = base, n = 1;
    while (used.has(id) || document.getElementById(id)) { n++; id = base + '-' + n; }
    used.add(id);
    h.id = id;
    h.dataset.tocId = id;
    h.setAttribute('tabindex', '-1');
    return id;
  }

  function headerH() {
    var v = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
    return isNaN(v) ? 60 : v;
  }
  function measure() {
    var y = window.scrollY;
    tops = headings.map(function (h) { return h.getBoundingClientRect().top + y; });
  }
  function readingLine() {
    var h = headerH();
    return window.scrollY + h + (window.innerHeight - h) * 0.25;
  }
  function atBottom() {
    return window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
  }

  function setActive(i) {
    if (i === activeIndex) return;
    activeIndex = i;
    for (var k = 0; k < headings.length; k++) {
      var on = (k === i);
      // every heading's id is the anchor; toggle on the matching <li> in BOTH lists
      var sel = 'a[href="#' + cssEscape(headings[k].id) + '"]';
      var anchors = document.querySelectorAll('[data-article-toc] ' + sel + ', [data-article-toc-mobile] ' + sel);
      for (var a = 0; a < anchors.length; a++) {
        var li = anchors[a].parentElement;
        if (on) { li.setAttribute('data-current', ''); anchors[a].setAttribute('aria-current', 'location'); }
        else { li.removeAttribute('data-current'); anchors[a].removeAttribute('aria-current'); }
      }
    }
    if (i >= 0 && links[i]) reveal(links[i]);
  }

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return s.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function reveal(link) {
    if (!nav || nav.matches(':hover') || nav.contains(document.activeElement)) return;
    var list = nav.querySelector('[data-article-toc-list]');
    if (!list) return;
    var pad = 24;
    var top = link.offsetTop - list.offsetTop;
    var bot = top + link.offsetHeight;
    if (top - pad < nav.scrollTop) nav.scrollTop = Math.max(0, top - pad);
    else if (bot + pad > nav.scrollTop + nav.clientHeight) nav.scrollTop = bot + pad - nav.clientHeight;
  }

  function update() {
    if (!spyOn || performance.now() < suppressUntil) return;
    var i;
    if (atBottom()) {
      i = headings.length - 1;
    } else {
      var line = readingLine();
      i = 0;
      for (var k = 0; k < tops.length; k++) { if (tops[k] <= line) i = k; else break; }
    }
    setActive(i);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; update(); });
  }

  function restoreFromHash() {
    var hash = decodeURIComponent(location.hash.slice(1));
    if (!hash) return false;
    var i = -1;
    for (var k = 0; k < headings.length; k++) { if (headings[k].id === hash) { i = k; break; } }
    if (i < 0) return false;
    window.scrollTo({ top: Math.max(0, headings[i].getBoundingClientRect().top + window.scrollY - headerH() - 24), behavior: 'auto' });
    setActive(i);
    suppressUntil = performance.now() + 300;
    return true;
  }

  function buildList(container) {
    var ol = container.querySelector('[data-article-toc-list]');
    if (!ol) return;
    ol.textContent = '';
    headings.forEach(function (h) {
      var li = document.createElement('li');
      li.className = 'wa-toc__item';
      var a = document.createElement('a');
      a.className = 'wa-toc__link';
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      a.setAttribute('data-text', h.textContent);
      li.appendChild(a);
      ol.appendChild(li);
      if (container === nav) links.push(a);
    });
    container.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var target = a.getAttribute('href').slice(1);
      var i = -1;
      for (var k = 0; k < headings.length; k++) { if (headings[k].id === target) { i = k; break; } }
      if (i < 0) return;
      setActive(i);
      suppressUntil = performance.now() + 1000;
      requestAnimationFrame(function () { headings[i].focus({ preventScroll: true }); });
      if (mobile && container === mobile) mobile.open = false;
    });
  }

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  function evalSpy() {
    spyOn = desktopMQ.matches;
    if (spyOn) { measure(); update(); }
  }

  function init() {
    try {
      var direct = Array.prototype.slice.call(prose.querySelectorAll(':scope > h2'));
      headings = direct.filter(function (h) { return h.textContent.trim(); });
      if (headings.length < 2) {
        headings = Array.prototype.slice.call(prose.querySelectorAll('h2')).filter(function (h) { return h.textContent.trim(); });
      }
      if (headings.length < MIN) return; // leave navs hidden; body widens to 2 columns

      used = new Set();
      links = [];
      activeIndex = -1;
      headings.forEach(assignId);

      if (nav) { buildList(nav); nav.hidden = false; }
      if (mobile) { buildList(mobile); mobile.hidden = false; }

      var layout = document.querySelector('.wa-body__layout');
      if (layout) layout.classList.add('wa-body__layout--ready');

      measure();
      if (!restoreFromHash()) update();

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', debounce(function () { measure(); update(); }, 150));
      if ('ResizeObserver' in window) new ResizeObserver(function () { measure(); update(); }).observe(prose);
      window.addEventListener('load', function () { measure(); update(); });
      window.addEventListener('pageshow', function (e) { if (e.persisted) { measure(); update(); } });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measure(); update(); });
      if ('onscrollend' in window) window.addEventListener('scrollend', function () { suppressUntil = 0; update(); });

      evalSpy();
      desktopMQ.addEventListener('change', evalSpy);
    } catch (e) {
      if (nav) nav.hidden = true;
      if (mobile) mobile.hidden = true;
    }
  }

  // Shopify theme editor re-renders the section; assignId is idempotent via data-toc-id.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-article-toc], [data-article-toc-mobile]')) {
      init();
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
